import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Calendar as CalendarIcon, Clock, ChevronLeft, CheckCircle2, AlertTriangle, User, Phone, Mail, MessageSquare } from 'lucide-react';
import API_URL from '../../config/api';

// ⭐ Página pública de reserva (Calendarios, pedido de Angelo 2026-08-07):
// esto es lo que ve un cliente sin cuenta al abrir /reservar/:slug -- mismo
// criterio sin-login que MenuPublico.js (comensal escaneando un QR), solo
// que acá el flujo tiene pasos (calendario -> fecha -> hora -> datos ->
// confirmación) en vez de una sola pantalla. Todo via axios plano + API_URL
// (no apiClient: no hay token, esta página nunca tuvo sesión).
//
// El backend (GET .../slots y POST .../book) es la única fuente de verdad
// sobre qué horarios existen -- este componente nunca calcula
// disponibilidad por su cuenta, solo pinta lo que el servidor le manda y
// vuelve a preguntar si el servidor rechaza la reserva (409 = alguien más
// tomó ese horario mientras tanto).

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-500 mx-auto mb-4" />
        <p className="text-gray-500">Cargando...</p>
      </div>
    </div>
  );
}

function NotFoundScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <div className="text-center max-w-sm">
        <AlertTriangle className="w-14 h-14 text-gray-300 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-gray-700 mb-1">Esta página de reservas no está disponible</h1>
        <p className="text-sm text-gray-500">Verifica el enlace con el negocio.</p>
      </div>
    </div>
  );
}

function todayYmd() {
  // Fecha de "hoy" en hora de Ecuador (UTC-5 fijo, sin horario de verano),
  // igual criterio que el resto del proyecto -- así el mínimo seleccionable
  // en el <input type="date"> no queda un día adelantado/atrasado según
  // dónde esté físicamente el navegador del cliente.
  const nowEc = new Date(Date.now() - 5 * 60 * 60 * 1000);
  return nowEc.toISOString().slice(0, 10);
}

const BookingPublico = ({ slug }) => {
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [business, setBusiness] = useState(null);
  const [calendars, setCalendars] = useState([]);

  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [selectedDate, setSelectedDate] = useState(todayYmd());
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [form, setForm] = useState({ customer_name: '', customer_phone: '', customer_email: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [confirmedAppointment, setConfirmedAppointment] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/public/booking/${slug}`)
      .then(res => {
        setBusiness({ name: res.data.business_name });
        setCalendars(res.data.calendars || []);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const loadSlots = useCallback((calendar, date) => {
    setLoadingSlots(true);
    setSlots([]);
    setSelectedSlot(null);
    axios.get(`${API_URL}/api/public/booking/${slug}/calendars/${calendar.id}/slots`, { params: { date } })
      .then(res => setSlots(res.data?.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [slug]);

  useEffect(() => {
    if (selectedCalendar) loadSlots(selectedCalendar, selectedDate);
  }, [selectedCalendar, selectedDate, loadSlots]);

  const handleSubmitBooking = async () => {
    const name = form.customer_name.trim();
    const phone = form.customer_phone.trim();
    if (!name) return setSubmitError('Escribe tu nombre.');
    if (!phone) return setSubmitError('Escribe tu teléfono.');
    setSubmitError('');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/public/booking/${slug}/calendars/${selectedCalendar.id}/book`, {
        customer_name: name,
        customer_phone: phone,
        customer_email: form.customer_email.trim(),
        notes: form.notes.trim(),
        start_at: selectedSlot
      });
      setConfirmedAppointment(res.data.appointment);
    } catch (error) {
      const msg = error.response?.data?.error || 'No se pudo confirmar la cita, intenta de nuevo.';
      setSubmitError(msg);
      // ⭐ 409 = el horario elegido ya no está disponible (alguien más lo
      // tomó, o cambió el horario del calendario mientras el cliente
      // llenaba el formulario) -- se refrescan los slots para que el
      // cliente vea la lista real actualizada en vez de reintentar a
      // ciegas contra el mismo horario que acaba de fallar.
      if (error.response?.status === 409) {
        loadSlots(selectedCalendar, selectedDate);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatSlotTime = (iso) => {
    const d = new Date(iso);
    return d.toLocaleTimeString('es-EC', { timeZone: 'America/Guayaquil', hour: '2-digit', minute: '2-digit' });
  };

  const formatConfirmedDateTime = (sqlDateTime) => {
    const d = new Date(String(sqlDateTime).replace(' ', 'T') + 'Z');
    return d.toLocaleString('es-EC', { timeZone: 'America/Guayaquil', dateStyle: 'full', timeStyle: 'short' });
  };

  if (loading) return <LoadingScreen />;
  if (notFound) return <NotFoundScreen />;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{business?.name}</h1>
          <p className="text-sm text-gray-500">Reserva tu cita en línea</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-5">
          {confirmedAppointment ? (
            <div className="text-center py-6">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-gray-900 mb-1">¡Cita confirmada!</h2>
              <p className="text-sm text-gray-600 mb-4">{confirmedAppointment.calendar_name}</p>
              <p className="text-sm font-semibold text-gray-800 capitalize">{formatConfirmedDateTime(confirmedAppointment.start_at)}</p>
              <p className="text-xs text-gray-400 mt-4">Guarda este mensaje como comprobante de tu reserva.</p>
            </div>
          ) : !selectedCalendar ? (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-gray-700 mb-3">Elige un servicio</p>
              {calendars.length === 0 ? (
                <p className="text-sm text-gray-400 italic">Este negocio todavía no tiene calendarios disponibles para reservar.</p>
              ) : calendars.map(cal => (
                <button
                  key={cal.id}
                  onClick={() => { setSelectedCalendar(cal); setSelectedDate(todayYmd()); }}
                  className="w-full text-left border rounded-lg p-3.5 hover:border-blue-400 hover:bg-blue-50 transition flex items-center gap-3"
                >
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: cal.color || '#1e3a8a' }} />
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-800">{cal.name}</p>
                    {cal.description && <p className="text-xs text-gray-500 truncate">{cal.description}</p>}
                    <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5"><Clock className="w-3 h-3" /> {cal.duration_minutes} min</p>
                  </div>
                </button>
              ))}
            </div>
          ) : !selectedSlot ? (
            <div className="space-y-4">
              <button onClick={() => setSelectedCalendar(null)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
                <ChevronLeft className="w-4 h-4" /> Elegir otro servicio
              </button>
              <div>
                <p className="font-semibold text-gray-800">{selectedCalendar.name}</p>
                <p className="text-xs text-gray-400">{selectedCalendar.duration_minutes} min</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 flex items-center gap-1 mb-1"><CalendarIcon className="w-3.5 h-3.5" /> Fecha</label>
                <input
                  type="date"
                  min={todayYmd()}
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-2">Horarios disponibles</p>
                {loadingSlots ? (
                  <p className="text-sm text-gray-400">Buscando horarios...</p>
                ) : slots.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">No hay horarios disponibles ese día. Prueba otra fecha.</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {slots.map(slot => (
                      <button
                        key={slot}
                        onClick={() => setSelectedSlot(slot)}
                        className="py-2 border rounded-lg text-sm font-medium text-gray-700 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50 transition"
                      >
                        {formatSlotTime(slot)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <button onClick={() => setSelectedSlot(null)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
                <ChevronLeft className="w-4 h-4" /> Elegir otro horario
              </button>
              <div className="bg-blue-50 rounded-lg p-3 text-sm">
                <p className="font-semibold text-gray-800">{selectedCalendar.name}</p>
                <p className="text-gray-600">{new Date(selectedDate + 'T00:00:00').toLocaleDateString('es-EC', { weekday: 'long', day: 'numeric', month: 'long' })} · {formatSlotTime(selectedSlot)}</p>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 flex items-center gap-1 mb-1"><User className="w-3.5 h-3.5" /> Nombre</label>
                  <input type="text" value={form.customer_name} onChange={(e) => setForm(f => ({ ...f, customer_name: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 flex items-center gap-1 mb-1"><Phone className="w-3.5 h-3.5" /> Teléfono (WhatsApp)</label>
                  <input type="tel" value={form.customer_phone} onChange={(e) => setForm(f => ({ ...f, customer_phone: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 flex items-center gap-1 mb-1"><Mail className="w-3.5 h-3.5" /> Email (opcional)</label>
                  <input type="email" value={form.customer_email} onChange={(e) => setForm(f => ({ ...f, customer_email: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 flex items-center gap-1 mb-1"><MessageSquare className="w-3.5 h-3.5" /> Notas (opcional)</label>
                  <textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
              </div>

              {submitError && <p className="text-sm text-red-600">{submitError}</p>}

              <button
                onClick={handleSubmitBooking}
                disabled={submitting}
                className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? 'Confirmando...' : 'Confirmar cita'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingPublico;
